import { Link } from '@tanstack/react-router'
import { BookOpen, Check, ExternalLink } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import type { Integration } from '../types/integration.types'

interface IntegrationCardProps {
  integration: Integration
  customDrawer?: React.ReactNode
}

export function IntegrationCard({
  integration,
  customDrawer,
}: IntegrationCardProps) {
  const _getStatusVariant = (status: string) => {
    switch (status) {
      case 'recommended':
        return 'default'
      case 'beta':
        return 'secondary'
      case 'new':
        return 'outline'
      case 'popular':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'recommended':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'beta':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'new':
        return 'bg-purple-100 text-purple-700 border-purple-200'
      case 'popular':
        return 'bg-orange-100 text-orange-700 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'billing':
        return 'Billing'
      case 'payments':
        return 'Payments'
      default:
        return category
    }
  }

  return (
    <Card className='h-full flex flex-col hover:shadow-md transition-shadow'>
      <CardHeader className='flex-shrink-0'>
        <div className='flex items-start justify-between'>
          <div className='flex items-center gap-3'>
            <div className='w-12 h-12 rounded-lg border bg-white flex items-center justify-center p-2'>
              <img
                src={integration.logo}
                alt={integration.logoAlt}
                className='w-8 h-8 object-contain'
                style={{ filter: 'brightness(0)' }}
              />
            </div>
            <div>
              <CardTitle className='text-lg'>{integration.name}</CardTitle>
              <div className='flex gap-1 mt-1'>
                {integration.category.map(cat => (
                  <Badge key={cat} variant='outline' className='text-xs'>
                    {getCategoryLabel(cat)}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-1'>
            {integration.status && (
              <Badge
                className={`text-xs capitalize ${getStatusColor(integration.status)}`}
              >
                {integration.status}
              </Badge>
            )}
            {integration.connected && (
              <Badge
                variant='outline'
                className='text-xs bg-green-50 text-green-700 border-green-200'
              >
                <Check className='w-3 h-3 mr-1' />
                Connected
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className='flex-1'>
        <CardDescription className='text-sm leading-relaxed'>
          {integration.description}
        </CardDescription>
      </CardContent>

      <CardFooter className='flex-shrink-0 pt-4 border-t'>
        <div className='flex gap-2 w-full'>
          {customDrawer ? (
            customDrawer
          ) : integration.connectUrl ? (
            <Button asChild className='flex-1' size='sm'>
              <Link to={integration.connectUrl}>
                {integration.connected ? 'Manage' : 'Connect'}
              </Link>
            </Button>
          ) : null}
          {integration.docsUrl && (
            <Button variant='outline' size='sm' asChild className='gap-2'>
              <a
                href={integration.docsUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center gap-2'
              >
                <BookOpen className='w-4 h-4' />
                Docs
                <ExternalLink className='w-3 h-3' />
              </a>
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  )
}
